import { useState, useEffect } from 'react';
import api from '../lib/api';
import { CreditCard, Receipt, ArrowDown, X as XIcon, CheckCircle, Warning, Clock, ArrowRight, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PLAN_ORDER = { free: 0, starter: 1, professional: 2, enterprise: 3 };

export default function BillingPage() {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [downgrading, setDowngrading] = useState(null);
  const [showCancel, setShowCancel] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [subRes, invRes, plansRes] = await Promise.all([
        api.get('/subscription/me'),
        api.get('/subscription/invoices'),
        api.get('/subscription/plans'),
      ]);
      setSubscription(subRes.data);
      setInvoices(invRes.data);
      setPlans(plansRes.data);
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally { setLoading(false); }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await api.post('/subscription/cancel');
      toast.success(res.data.message);
      setShowCancel(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally { setCancelling(false); }
  };

  const handleDowngrade = async (planId) => {
    setDowngrading(planId);
    try {
      const res = await api.post('/subscription/downgrade', { plan_id: planId });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to downgrade');
    } finally { setDowngrading(null); }
  };

  const handleUpgrade = (planId) => {
    window.location.href = `/payment?plan=${planId}`;
  };

  const getStatusStyle = (status) => {
    const map = {
      paid: { bg: 'bg-[#00A859]/10 text-[#00A859] border-[#00A859]/30', icon: CheckCircle },
      pending: { bg: 'bg-[#FFC107]/10 text-[#FFC107] border-[#FFC107]/30', icon: Clock },
      cancelled: { bg: 'bg-[#FF2B2B]/10 text-[#FF2B2B] border-[#FF2B2B]/30', icon: XIcon },
      downgraded: { bg: 'bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/30', icon: ArrowDown },
      expired: { bg: 'bg-black/5 text-[#8F8F8F] border-black/10', icon: Warning },
    };
    return map[status] || map.pending;
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const currentPlanOrder = PLAN_ORDER[subscription?.plan_id] ?? 0;
  const isAdmin = subscription?.role === 'admin' || subscription?.role === 'assistant';

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-black/5 w-48" />
          <div className="h-40 bg-white border border-black/15" />
          <div className="h-64 bg-white border border-black/15" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="billing-page">
      <div className="mb-8">
        <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00]">Account</p>
        <h1 className="font-sans text-3xl md:text-4xl font-black tracking-tight mt-1">Billing & Invoices</h1>
      </div>

      {/* Current Plan */}
      <div className="bg-white border border-black/15 mb-6" data-testid="current-plan-card">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FF5A00] flex items-center justify-center">
              <CreditCard size={24} weight="bold" className="text-white" />
            </div>
            <div>
              <p className="font-sans text-xl font-bold tracking-tight">{subscription?.plan_name || 'Free'} Plan</p>
              <p className="font-mono text-xs text-[#5E5E5E] mt-0.5">
                {isAdmin ? 'Admin / Assistant — Unlimited free access' :
                  subscription?.plan_id === 'free' ? '3 analyses per month' :
                  `${subscription?.plan_price} SAR/month — ${subscription?.analyses_limit === 999999 ? 'Unlimited' : subscription?.analyses_limit} analyses`}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-mono text-xs text-[#8F8F8F]">Usage this month</p>
            <p className="font-sans text-2xl font-black">
              {subscription?.analyses_used || 0}
              <span className="text-sm text-[#8F8F8F] font-mono">/{subscription?.analyses_limit === 999999 ? '∞' : subscription?.analyses_limit}</span>
            </p>
          </div>
        </div>
        {!isAdmin && subscription?.plan_id !== 'free' && (
          <div className="px-6 py-3 border-t border-black/10 bg-[#F7F7F7] flex items-center justify-between">
            <p className="font-mono text-xs text-[#5E5E5E]">
              Manage your subscription
            </p>
            <button
              onClick={() => setShowCancel(true)}
              data-testid="cancel-subscription-btn"
              className="font-mono text-xs uppercase tracking-wide text-[#FF2B2B] hover:underline"
            >
              Cancel Subscription
            </button>
          </div>
        )}
      </div>

      {/* Plans Grid (Upgrade / Downgrade) */}
      {!isAdmin && (
        <div className="mb-8">
          <h2 className="font-sans text-xl font-bold tracking-tight mb-4">Change Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 border border-black/15">
            {Object.entries(plans).map(([id, plan], i) => {
              const order = PLAN_ORDER[id] ?? 0;
              const isCurrent = id === subscription?.plan_id;
              const isUpgrade = order > currentPlanOrder;
              const isDowngrade = order < currentPlanOrder;
              return (
                <div key={id} className={`p-5 ${i < 3 ? 'md:border-r border-b md:border-b-0 border-black/15' : ''} ${isCurrent ? 'bg-[#F7F7F7]' : 'bg-white'}`} data-testid={`plan-option-${id}`}>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#8F8F8F] mb-1">{plan.name}</p>
                  <p className="font-sans text-2xl font-black">{plan.price}<span className="font-mono text-xs text-[#8F8F8F]"> SAR</span></p>
                  <p className="font-mono text-[10px] text-[#5E5E5E] mt-1 mb-4">
                    {plan.analyses_per_month === 999999 ? 'Unlimited' : plan.analyses_per_month} analyses/mo
                  </p>
                  {isCurrent ? (
                    <span className="block text-center py-2 px-4 font-mono text-xs uppercase tracking-wide bg-black text-white">Current Plan</span>
                  ) : isUpgrade ? (
                    <button
                      onClick={() => id === 'enterprise' ? null : handleUpgrade(id)}
                      data-testid={`upgrade-to-${id}`}
                      className="block w-full text-center py-2 px-4 font-mono text-xs uppercase tracking-wide bg-[#FF5A00] text-white hover:bg-[#E04E00] transition-colors"
                    >
                      {id === 'enterprise' ? 'Contact Sales' : 'Upgrade'}
                    </button>
                  ) : isDowngrade ? (
                    <button
                      onClick={() => handleDowngrade(id)}
                      disabled={downgrading === id}
                      data-testid={`downgrade-to-${id}`}
                      className="block w-full text-center py-2 px-4 font-mono text-xs uppercase tracking-wide border border-black/30 text-[#5E5E5E] hover:border-black hover:text-black transition-colors disabled:opacity-50"
                    >
                      {downgrading === id ? 'Processing...' : 'Downgrade'}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invoice History */}
      <div className="bg-white border border-black/15" data-testid="invoice-history">
        <div className="p-6 border-b border-black/15 flex items-center gap-3">
          <Receipt size={20} weight="regular" className="text-[#FF5A00]" />
          <h2 className="font-sans text-xl font-bold tracking-tight">Invoice History</h2>
        </div>
        {invoices.length > 0 ? (
          <div>
            <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 p-4 bg-[#F7F7F7] border-b border-black/15 font-mono text-xs uppercase tracking-wider font-bold">
              <span>Description</span>
              <span>Amount</span>
              <span>Status</span>
              <span>Date</span>
              <span>ID</span>
            </div>
            {invoices.map((inv, i) => {
              const st = getStatusStyle(inv.payment_status);
              return (
                <div key={inv.session_id || i} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center p-4 border-b border-black/10 hover:bg-[#F7F7F7] transition-colors" data-testid={`invoice-row-${i}`}>
                  <div>
                    <p className="font-mono text-sm font-medium">{inv.plan_name}</p>
                    {inv.metadata?.action && (
                      <p className="font-mono text-[10px] text-[#8F8F8F]">
                        {inv.metadata.action === 'cancellation' ? `Cancelled from ${inv.metadata.previous_plan}` :
                         inv.metadata.action === 'downgrade' ? `From ${inv.metadata.previous_plan} to ${inv.metadata.new_plan}` :
                         inv.email}
                      </p>
                    )}
                  </div>
                  <span className="font-mono text-sm font-bold">
                    {inv.amount > 0 ? `${inv.amount} ${inv.currency?.toUpperCase()}` : '-'}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border ${st.bg}`}>
                    <st.icon size={12} weight="regular" />
                    {inv.payment_status}
                  </span>
                  <span className="font-mono text-xs text-[#8F8F8F]">{formatDate(inv.created_at)}</span>
                  <span className="font-mono text-[10px] text-[#8F8F8F] truncate max-w-[120px]">{inv.session_id?.slice(0, 20)}...</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Receipt size={40} weight="regular" className="text-[#8F8F8F] mx-auto mb-3" />
            <p className="font-sans text-lg font-bold mb-1">No Invoices Yet</p>
            <p className="font-mono text-xs text-[#5E5E5E]">Your payment history will appear here</p>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cancel-modal">
          <div className="bg-white border border-black/15 w-full max-w-md">
            <div className="p-6 border-b border-black/15">
              <h3 className="font-sans text-lg font-bold text-[#FF2B2B]">Cancel Subscription</h3>
            </div>
            <div className="p-6">
              <p className="font-mono text-sm text-[#5E5E5E] mb-2">
                Are you sure you want to cancel your <strong>{subscription?.plan_name}</strong> plan?
              </p>
              <ul className="font-mono text-xs text-[#8F8F8F] space-y-1 mb-6">
                <li>- You will be reverted to the Free plan (3 analyses/month)</li>
                <li>- Excel and PDF exports will be disabled</li>
                <li>- Your existing projects and data will be kept</li>
              </ul>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  data-testid="confirm-cancel-btn"
                  className="flex-1 py-3 bg-[#FF2B2B] text-white font-mono text-sm uppercase tracking-wide hover:bg-[#CC2222] transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                </button>
                <button
                  onClick={() => setShowCancel(false)}
                  data-testid="keep-plan-btn"
                  className="flex-1 py-3 border border-black text-black font-mono text-sm uppercase tracking-wide hover:bg-black hover:text-white transition-colors"
                >
                  Keep Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
